"use client"

import React, {
  useCallback,
  useEffect,
  useState,
  MouseEventHandler,
  ReactNode,
  useRef,
} from "react"
import Image from "next/image"
import * as AspectRatioPrimitive from "@radix-ui/react-aspect-ratio"
import ReactPlayer from "react-player"
import { useIsMobile } from "@/hooks/use-is-mobile"
import styled from "@emotion/styled"
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel-pro"
import Lightbox from "react-image-lightbox"
import { placeHolderImage } from "@/constants/placeHolderImage"

const AspectRatio = AspectRatioPrimitive.Root

interface Props {
  images: any[]
  videoUrl: string
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  productName: string
}

const MainImageCarousel = (props: Props) => {
  const { images, videoUrl, activeIndex, setActiveIndex, productName } = props

  const isYoutubeUrl =
    !!videoUrl &&
    (videoUrl.indexOf("https://youtu.be") === 0 ||
      videoUrl.indexOf("https://youtube.com") === 0 ||
      videoUrl.indexOf("https://www.youtube.com") === 0)

  const reactPlayerRef = useRef<null | ReactPlayer>(null)
  const { isMobile } = useIsMobile()
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)
  const [activeDotIndex, setActiveDotIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [bigImagePositionPercent, setBigImagePositionPercent] = useState({
    left: 0,
    top: 0,
  })
  const [lightBoxState, setLightBoxState] = useState({
    open: false,
    index: 0,
  })
  const [showVideoCover, setShowVideoCover] = useState(true)
  const prevPropsRef = useRef({ activeIndex })
  const isDesktopVideoPlayedOnceRef = useRef(false)
  const isVideoPlayingRef = useRef(false)

  const handleReactPlayerPlay = () => {
    const player = reactPlayerRef.current?.getInternalPlayer()

    if (!player) {
      return
    }

    if (player instanceof HTMLVideoElement) {
      player.play()
      return
    }

    if (typeof player === "object") {
      reactPlayerRef.current?.getInternalPlayer()?.playVideo?.()
    }
  }

  const handleReactPlayerPause = () => {
    const player = reactPlayerRef.current?.getInternalPlayer()

    if (!player) {
      return
    }

    if (player instanceof HTMLVideoElement) {
      player.pause()
      return
    }

    if (typeof player === "object") {
      reactPlayerRef.current?.getInternalPlayer()?.pauseVideo?.()
    }
  }

  if (prevPropsRef.current.activeIndex !== activeIndex) {
    prevPropsRef.current.activeIndex = activeIndex

    if (isMobile && videoUrl && !showVideoCover) {
      setShowVideoCover(true)
      handleReactPlayerPause()
    }

    if (!isMobile && videoUrl && isVideoPlayingRef.current) {
      handleReactPlayerPause()
      isVideoPlayingRef.current = false
    }
  }

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    carouselApi?.scrollTo(activeIndex, false)
  }, [activeIndex, carouselApi])

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    const handler = (api) => {
      const curIndex = api.selectedScrollSnap()
      setActiveIndex(curIndex)
    }

    carouselApi.on("select", handler)

    return () => {
      carouselApi.off("select", handler)
    }
  }, [carouselApi, setActiveIndex])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!carouselApi) {
        return
      }
      carouselApi.scrollTo(index)
    },
    [carouselApi]
  )

  const onInit = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) {
      return
    }

    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) {
      return
    }

    setActiveDotIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    onInit(carouselApi)
    onSelect(carouselApi)
    carouselApi
      .on("reInit", onInit)
      .on("reInit", onSelect)
      .on("select", onSelect)
  }, [carouselApi, onInit, onSelect])

  const handleBigImageCoverMousemove: MouseEventHandler<HTMLDivElement> = (
    e
  ) => {
    if (isMobile) {
      return
    }

    const eventCurrentTarget = e.currentTarget
    const nativeEvent = e.nativeEvent

    setBigImagePositionPercent({
      left: (nativeEvent.offsetX / eventCurrentTarget.offsetWidth) * 100,
      top: (nativeEvent.offsetY / eventCurrentTarget.offsetHeight) * 100,
    })
  }

  const handlePlayVideo = () => {
    setShowVideoCover(false)
    handleReactPlayerPlay()
    isVideoPlayingRef.current = true

    if (!isMobile) {
      isDesktopVideoPlayedOnceRef.current = true
    }
  }

  const handlePauseVideo = () => {
    handleReactPlayerPause()
    isVideoPlayingRef.current = false

    if (isMobile) {
      setShowVideoCover(true)
      return
    }

    if (!isDesktopVideoPlayedOnceRef.current) {
      setShowVideoCover(true)
    }
  }

  const handleReactPlayerOnPause = () => {
    if (isMobile) {
      setShowVideoCover(true)
    }
  }

  const handleReactPlayerOnPlay = () => {
    isVideoPlayingRef.current = true
  }

  return (
    <div
      style={{
        fontSize: 0,
        position: "relative",
        flex: 1,
        maxWidth: 720,
      }}
    >
      {images.length > 1 && !isMobile ? (
        <>
          <SlideArrow
            style={{ left: 0 }}
            onClick={() => {
              carouselApi?.scrollPrev()
            }}
          >
            <svg
              width="10"
              height="16"
              viewBox="0 0 10 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 1.5L1.5 8L8.5 14.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </SlideArrow>
          <SlideArrow
            style={{ right: 0 }}
            onClick={() => {
              carouselApi?.scrollNext()
            }}
          >
            <svg
              width="9"
              height="15"
              viewBox="0 0 9 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 14L8 7.5L1 1"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </SlideArrow>
        </>
      ) : null}
      <Carousel
        opts={{ loop: true }}
        setApi={setCarouselApi}
        autoPlayOpts={{ playOnInit: false }}
      >
        <CarouselContent
          style={{
            display: "flex",
            maxWidth: 762,
            width: "100%",
            marginLeft: 0,
          }}
        >
          {images.reduce((acc, item, index, arr) => {
            const imageAlt = [`Image${index + 1}`].filter(Boolean).join(" ")

            acc.push(
              <CarouselItem
                key={index}
                style={{
                  flex: "0 0 100%",
                  paddingLeft: 0,
                }}
              >
                <div className="group relative flex-shrink overflow-hidden lg_hover_bg-no-repeat">
                  <Image
                    src={item.image}
                    width={1500}
                    height={1500}
                    style={{ width: "100%", height: "auto" }}
                    alt={imageAlt}
                    title={imageAlt}
                    loader={({ src }) => src}
                    unoptimized
                    placeholder={placeHolderImage}
                  />
                </div>
              </CarouselItem>
            )

            if (videoUrl && index === 0) {
              acc.push(
                <CarouselItem
                  key={arr.length}
                  style={{
                    flex: "0 0 100%",
                    paddingLeft: 0,
                  }}
                >
                  <AspectRatio ratio={1500 / 1500}>
                    {isYoutubeUrl ? (
                      <button
                        type="button"
                        className="top-15 absolute right-3 m-0 flex h-10 w-10 items-center justify-center rounded-full border-none bg-white/30 p-0 outline-none lg_hidden"
                        onClick={handlePauseVideo}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="none"
                          viewBox="0 0 18 18"
                        >
                          <path
                            stroke="#fff"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M17 17 1 1m16 0L1 17"
                          ></path>
                        </svg>
                      </button>
                    ) : null}

                    {showVideoCover ? (
                      <>
                        <div className="z-2 absolute left-0 top-0 h-full w-full cursor-pointer overflow-hidden">
                          <div className="absolute left-0 top-0 h-full w-full backdrop-blur-sm"></div>
                          <Image
                            src={arr[0].image}
                            width={1500}
                            height={1500}
                            className="h-full w-full object-cover"
                            alt={imageAlt}
                            title={imageAlt}
                            onClick={handlePlayVideo}
                            loader={({ src }) => src}
                            unoptimized
                            placeholder={placeHolderImage}
                          />
                        </div>
                        <button
                          type="button"
                          className="z-3 border-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer bg-transparent p-0 outline-none"
                          onClick={handlePlayVideo}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="50"
                            height="50"
                            fill="none"
                            viewBox="0 0 50 50"
                            className="h-auto w-[60px] lg_w-[100px]"
                          >
                            <circle
                              cx="25"
                              cy="25"
                              r="24"
                              stroke="#fff"
                              strokeWidth="2"
                            ></circle>
                            <path
                              fill="#fff"
                              stroke="#fff"
                              d="M33.75 23.701c1 .577 1 2.02 0 2.598l-12 6.928a1.5 1.5 0 0 1-2.25-1.299V18.072a1.5 1.5 0 0 1 2.25-1.3z"
                            ></path>
                          </svg>
                        </button>
                      </>
                    ) : null}

                    <ReactPlayer
                      url={videoUrl}
                      loop={false}
                      controls
                      playbackRate={1}
                      width={"100%"}
                      height={"100%"}
                      playsinline
                      ref={reactPlayerRef}
                      onPause={handleReactPlayerOnPause}
                      onPlay={handleReactPlayerOnPlay}
                    />
                  </AspectRatio>
                </CarouselItem>
              )
            }

            return acc
          }, [])}
        </CarouselContent>
        {isMobile &&
        scrollSnaps.length > 0 &&
        (!videoUrl ||
          (videoUrl && activeIndex !== 1) ||
          (videoUrl && activeIndex === 1 && showVideoCover)) ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 0,
              columnGap: 8,
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {scrollSnaps.map((_, i) => {
              const isActive = activeDotIndex === i

              return (
                <button
                  type="button"
                  onClick={() => {
                    onDotButtonClick(i)
                  }}
                  key={i}
                  style={{
                    width: isActive ? 24 : 8,
                    height: 8,
                    borderRadius: 8,
                    flex: "0 0 auto",
                    outline: "none",
                    border: "none",
                    fontSize: 0,
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? "#f2e0d6"
                      : "rgba(242, 224, 214,.5)",
                    transition: "width linear 0.3s",
                  }}
                />
              )
            })}
          </div>
        ) : null}
      </Carousel>

      {lightBoxState.open && (
        <Lightbox
          mainSrc={images[lightBoxState.index]?.image}
          nextSrc={images[lightBoxState.index + 1]?.image}
          prevSrc={images[lightBoxState.index - 1]?.image}
          imageCaption={`${lightBoxState.index + 1} of ${images.length}`}
          enableZoom
          onCloseRequest={() => {
            setLightBoxState((x) => ({ ...x, open: false }))
          }}
          onMovePrevRequest={() => {
            setLightBoxState((x) => ({ ...x, index: x.index - 1 }))
          }}
          onMoveNextRequest={() => {
            setLightBoxState((x) => ({ ...x, index: x.index + 1 }))
          }}
        />
      )}
    </div>
  )
}

const SlideArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 46px;
  height: 46px;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  z-index: 6;
  border: none;
  outline: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  color: #666;
  border-radius: 10px;
  border: 1.5px solid #adadad;

  :hover {
    background-color: #2f6132;
    color: white;
  }
`

export default MainImageCarousel
